import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { NEVER, Observable, Subject, map } from 'rxjs';

import { ButtonMode, ButtonSize, ButtonTheme, IconPosition, ModalButtons } from '@fcom/ui-components';
import { BoundType, SelectedServiceData } from '@fcom/dapi';
import { ElementActions, ElementTypes, FragmentService, GaContext } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import {
  Category,
  FinnairBoundItem,
  FinnairCheckInVersion,
  FinnairDisruptedBoundItem,
  FinnairOrder,
  FinnairTravelEndpoint,
} from '@fcom/dapi/api/models';
import { RedirectService } from '@fcom/common-booking/modules/redirect-service';
import {
  bookingHasDisruption,
  getTails,
  isBoundFlown,
  isByBusOnly,
  passengersMissingCategoryService,
} from '@fcom/common-booking/utils/common-booking.utils';
import { UtilsService } from '@fcom/common-booking/services/utils.service';
import { CmsArticle } from '@fcom/core-api';
import { LanguageService } from '@fcom/ui-translate';
import { snapshot } from '@fcom/rx';
import { Airport } from '@fcom/common/interfaces/booking';

import { BoundStatus } from '../../../../enums';
import { NotificationsService } from '../../../../services/notifications.service';
import { Notifications } from '../../../../interfaces';
import { isDisruptedBound } from '../../../../utils';

const CHECKIN_ARTICLE_URL = 'MMB.checkIn.fragments.upsellModal.url';

@Component({
  selector: 'fin-mmb-flight',
  templateUrl: './flight.component.html',
  styleUrls: ['./flight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingFlightComponent implements OnInit {
  readonly Category = Category;
  readonly ModalButtons = ModalButtons;
  readonly BoundType = BoundType;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  enableNewCheckin: boolean;
  isFlown = false;
  isDisrupted = false;
  disruptedLabel = '';
  itineraryModalOpen = false;
  checkInOpen = false;
  notifications$: Observable<Notifications>;
  viewDetailsClosed$: Subject<boolean> = new Subject();
  tails: string[];
  isByBusOnly: boolean;
  showViewDetails: boolean;
  checkInModalArticle$: Observable<CmsArticle>;
  checkInModalOpen = false;
  checkInModalClosed$: Subject<boolean> = new Subject<boolean>();

  /**
   * Flight Bound Information
   */
  @Input() bound: FinnairBoundItem;
  /**
   * A map of the locations in the bound/booking to get city and airport names
   */
  @Input() locations: FinnairOrder['locations'];

  @Input() order: FinnairOrder;

  /**
   * Type of bound to render in the timeline
   */
  @Input() type: BoundType = null;

  @Input() isComplexJourney = false;

  @Output() emailModalOpen: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  upsellClicked: EventEmitter<SelectedServiceData> = new EventEmitter<SelectedServiceData>();

  disruptedOrOriginalBound: FinnairBoundItem | FinnairDisruptedBoundItem;

  constructor(
    private utilsService: UtilsService,
    private notificationsService: NotificationsService,
    private redirectService: RedirectService,
    private gtmService: GtmService,
    private fragmentService: FragmentService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.disruptedOrOriginalBound = this.bound.disruptedBound?.disruptedBound ?? this.bound;
    this.isFlown = isBoundFlown(this.disruptedOrOriginalBound);
    this.isDisrupted = isDisruptedBound(this.bound) && !this.isFlown;
    this.disruptedLabel = this.isDisrupted ? 'MMB.reaccommodation.notes.AFFECTED' : '';
    this.enableNewCheckin =
      this.order?.eligibilities?.checkIn?.find((eligibility) => eligibility.id === this.bound.id)?.version ===
      FinnairCheckInVersion.NEXTGEN;
    this.checkInOpen =
      this.order?.eligibilities?.checkIn?.find((eligibility) => eligibility.id === this.bound.id)?.isAllowedToUse ??
      false;
    this.checkInModalArticle$ = this.getCheckInArticleStream(this.checkInOpen, this.order, this.bound);

    this.notifications$ = this.notificationsService.getNotificationsForBound(
      this.order,
      this.bound,
      BoundStatus.DEFAULT
    );
    this.tails = getTails(this.disruptedOrOriginalBound);
    this.isByBusOnly = isByBusOnly(this.disruptedOrOriginalBound);
    this.showViewDetails = !bookingHasDisruption(this.order);
  }

  getLocation(leg: FinnairTravelEndpoint): Observable<Airport> {
    return this.utilsService.getLocation(leg.locationCode, leg.dateTime);
  }

  openViewDetails(): void {
    this.itineraryModalOpen = true;
  }

  private getCheckInArticleStream(
    checkInOpen: boolean,
    order: FinnairOrder,
    bound: FinnairBoundItem
  ): Observable<CmsArticle> {
    if (checkInOpen && passengersMissingCategoryService(Category.SEAT, order, bound)) {
      return this.fragmentService.getFragment(CHECKIN_ARTICLE_URL);
    }
    return NEVER;
  }

  private trackElement(element: string): void {
    this.gtmService.trackElement(
      `view-pnr_${element}`,
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK,
      `recLoc-${this.order.otherInformation.analyticsToken}`
    );
  }

  emitUpsell(category: Category): void {
    this.upsellClicked.emit({ category, fragmentId: this.bound.id });
    this.checkInModalOpen = false;
  }

  redirectToCheckIn(bound: FinnairBoundItem | FinnairDisruptedBoundItem, forceRedirect = false): void {
    this.trackElement('checkIn');

    const hasArticle = snapshot(
      this.languageService
        .translate(CHECKIN_ARTICLE_URL, undefined, { logErrors: false })
        .pipe(map((str) => Boolean(str)))
    );

    if (forceRedirect || !hasArticle) {
      this.redirectService.redirectToCheckIn(this.order, bound, true);
      return;
    }

    if (passengersMissingCategoryService(Category.SEAT, this.order, bound)) {
      this.checkInModalOpen = true;
    } else {
      this.redirectService.redirectToCheckIn(this.order, bound, true);
    }
  }
}
